import * as THREE from "three";

interface TextureManagerOptions {
  maxCacheSize?: number;
  onTextureLoad?: (frameName: string, texture: THREE.Texture) => void;
  onTextureError?: (frameName: string, error: any) => void;
}

class LRUNode {
  constructor(
    public key: string,
    public value: THREE.Texture,
    public prev: LRUNode | null = null,
    public next: LRUNode | null = null,
  ) {}
}

class LRUCache {
  private capacity: number;
  private cache = new Map<string, LRUNode>();
  private head: LRUNode;
  private tail: LRUNode;

  constructor(capacity: number) {
    this.capacity = capacity;
    // Create dummy head and tail nodes
    this.head = new LRUNode("", null as any);
    this.tail = new LRUNode("", null as any);
    this.head.next = this.tail;
    this.tail.prev = this.head;
  }

  private addToHead(node: LRUNode): void {
    node.prev = this.head;
    node.next = this.head.next;
    this.head.next!.prev = node;
    this.head.next = node;
  }

  private removeNode(node: LRUNode): void {
    node.prev!.next = node.next;
    node.next!.prev = node.prev;
  }

  private moveToHead(node: LRUNode): void {
    this.removeNode(node);
    this.addToHead(node);
  }

  private removeTail(): LRUNode {
    const lastNode = this.tail.prev!;
    this.removeNode(lastNode);
    return lastNode;
  }

  get(key: string): THREE.Texture | null {
    const node = this.cache.get(key);
    if (node) {
      // Move to head (mark as recently used)
      this.moveToHead(node);
      return node.value;
    }
    return null;
  }

  put(key: string, value: THREE.Texture): THREE.Texture | null {
    const existingNode = this.cache.get(key);

    if (existingNode) {
      // Update existing node
      existingNode.value = value;
      this.moveToHead(existingNode);
      return null;
    }

    const newNode = new LRUNode(key, value);

    if (this.cache.size >= this.capacity) {
      // Remove least recently used
      const tail = this.removeTail();
      this.cache.delete(tail.key);
      const evictedTexture = tail.value;

      // Add new node
      this.cache.set(key, newNode);
      this.addToHead(newNode);

      return evictedTexture;
    } else {
      // Add new node
      this.cache.set(key, newNode);
      this.addToHead(newNode);
      return null;
    }
  }

  clear(): THREE.Texture[] {
    const textures: THREE.Texture[] = [];
    for (const node of this.cache.values()) {
      textures.push(node.value);
    }
    this.cache.clear();
    this.head.next = this.tail;
    this.tail.prev = this.head;
    return textures;
  }

  size(): number {
    return this.cache.size;
  }

  has(key: string): boolean {
    return this.cache.has(key);
  }
}

export class TextureManager {
  private textureCache: LRUCache;
  private loadingPromises = new Map<string, Promise<THREE.Texture>>();
  private textureLoader = new THREE.TextureLoader();

  constructor(
    private framesPath: string,
    private options: TextureManagerOptions = {},
  ) {
    this.textureCache = new LRUCache(options.maxCacheSize || 100);
  }

  async getTexture(frameName: string): Promise<THREE.Texture> {
    // Check if texture is already cached
    const cachedTexture = this.textureCache.get(frameName);
    if (cachedTexture) {
      return cachedTexture;
    }

    // Check if texture is currently loading
    if (this.loadingPromises.has(frameName)) {
      return this.loadingPromises.get(frameName)!;
    }

    // Start loading the texture
    const loadingPromise = this.loadTexture(frameName);
    this.loadingPromises.set(frameName, loadingPromise);

    try {
      const texture = await loadingPromise;
      this.loadingPromises.delete(frameName);
      this.cacheTexture(frameName, texture);
      return texture;
    } catch (error) {
      this.loadingPromises.delete(frameName);
      throw error;
    }
  }

  private async loadTexture(frameName: string): Promise<THREE.Texture> {
    return new Promise((resolve, reject) => {
      const framePath = `${this.framesPath}/${frameName}`;

      this.textureLoader.load(
        framePath,
        (texture) => {
          texture.colorSpace = THREE.SRGBColorSpace;
          texture.generateMipmaps = false;
          texture.minFilter = THREE.LinearFilter;
          texture.magFilter = THREE.LinearFilter;

          this.options.onTextureLoad?.(frameName, texture);
          resolve(texture);
        },
        undefined,
        (error) => {
          this.options.onTextureError?.(frameName, error);
          reject(error);
        },
      );
    });
  }

  private cacheTexture(frameName: string, texture: THREE.Texture): void {
    const evictedTexture = this.textureCache.put(frameName, texture);
    if (evictedTexture) {
      evictedTexture.dispose();
    }
  }

  preloadTextures(frameNames: string[]): Promise<PromiseSettledResult<void | THREE.Texture>[]> {
    const promises = frameNames.map((frameName) =>
      this.getTexture(frameName).catch((error) => {
        console.warn(`Failed to preload texture: ${frameName}`, error);
      }),
    );

    return Promise.allSettled(promises);
  }

  dispose(): void {
    const allTextures = this.textureCache.clear();
    allTextures.forEach((texture) => texture.dispose());
    this.loadingPromises.clear();
  }

  getCacheSize(): number {
    return this.textureCache.size();
  }

  isLoading(frameName: string): boolean {
    return this.loadingPromises.has(frameName);
  }
}
